import { useState, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { Menu, X, Search } from "lucide-react";
import MemoireLogo from "@/components/MemoireLogo";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const link = (id, label, key) => (
    <a
      key={id}
      href={`#${id}`}
      data-testid={`nav-link-${key}`}
      className="text-[13px] uppercase tracking-[0.18em] text-neutral-700 transition-colors hover:text-neutral-950"
    >
      {label}
    </a>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-neutral-200/60 bg-white/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-8">
        <a href="#hero" className="group">
          <MemoireLogo
            data-testid="brand-logo"
            variant="horizontal"
            gold="#8B6F3E"
            wordmarkColor="#1A1A1A"
            size={38}
          />
        </a>

        <nav className="hidden items-center gap-10 md:flex">
          {link("hero", t.nav.home, "home")}
          {link("occasions", t.nav.event, "event")}
          {link("why", t.nav.why, "why")}
          {link("reach", t.nav.reach, "reach")}
        </nav>

        <div className="hidden items-center gap-5 md:flex">
          <Search className="h-4 w-4 cursor-pointer text-neutral-700" strokeWidth={1.5} />
          <button
            data-testid="language-toggle-btn"
            onClick={toggle}
            className="group relative flex items-center gap-1 rounded-full border border-neutral-300 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-neutral-800 transition-all hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
          >
            <span className={lang === "en" ? "" : "opacity-40"}>EN</span>
            <span className="text-neutral-400">/</span>
            <span className={lang === "id" ? "" : "opacity-40"}>ID</span>
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-neutral-200 bg-white md:hidden">
          <nav className="flex flex-col gap-5 px-6 py-6">
            {link("hero", t.nav.home, "home-m")}
            {link("occasions", t.nav.event, "event-m")}
            {link("why", t.nav.why, "why-m")}
            {link("reach", t.nav.reach, "reach-m")}
            <button
              data-testid="language-toggle-btn-mobile"
              onClick={toggle}
              className="self-start rounded-full border border-neutral-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em]"
            >
              {lang === "en" ? "EN / id" : "en / ID"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
